/**
 * 个股点击指令
 * 用于给高亮的个股添加点击事件
 */

import { createPopper } from '@popperjs/core';
import CustomStocksPopover from '@/components/customStocksPopover/index.vue';
import { createApp } from 'vue';
import i18n from '@/plugins/i18n';
import ElementUI from 'element-plus';
import router from '@/router';
import store from '@/store';
import directives from '@/common/directive';
import { throttle } from 'lodash-es';

const stockClick = {
  // 显示浮层的延时
  showDelay: 300,
  // 隐藏浮层的延时
  hideDelay: 300,
  // 定时器 显示
  showDelayTimer: null,
  // 定时器 隐藏
  delayTimer: null,
  // 是否存在浮层
  hasPopover: false,
  // 销毁浮层
  destroyPopover(el, outside = false) {
    // 清除显示延迟定时器
    if (stockClick.showDelayTimer) {
      clearTimeout(stockClick.showDelayTimer);
      stockClick.showDelayTimer = null;
    }

    if (el.__popoverApp__) {
      el.__popoverApp__.unmount();
      el.__popoverApp__ = null;
    }
    if (el.__popoverEl__) {
      el.removeEventListener('mouseenter', el.__popoverEl__mouseenter__);
      el.removeEventListener('mouseleave', el.__popoverEl__mouseleave__);
      el.__popoverEl__.remove();
      el.__popoverEl__ = null;
    }
    if (el.__popperInstance__) {
      el.__popperInstance__.destroy();
      el.__popperInstance__ = null;
    }
    if (el.__outsideHandler__) {
      document.removeEventListener('click', el.__outsideHandler__);
      el.__outsideHandler__ = null;
    }
    el.__popoverTarget__ = null;

    if (el.__mouseover__ && !outside) {
      el.removeEventListener('mouseover', el.__mouseover__);
      el.__mouseover__ = null;
    }
    if (el.__mouseout__ && !outside) {
      el.removeEventListener('mouseout', el.__mouseout__);
      el.__mouseout__ = null;
    }
    if (el.__resizeObserver__) {
      el.__resizeObserver__.disconnect();
      el.__resizeObserver__ = null;
    }

    stockClick.hasPopover = false;
  },
  mounted(el, binding) {
    const popperOptions = binding.value.popperOptions || {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            rootBoundary: 'viewport', // 以视口为边界
            padding: 16, // 留边距避免贴边
            tether: false
          }
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['left-start', 'right-start'], // 无法放置时的备选位置
            flipVariations: false
          }
        },
        // 新增：位置变化时添加平滑过渡
        {
          name: 'transition',
          options: {
            duration: 100, // 过渡时间
            easing: 'ease-out'
          }
        }
      ]
    };

    // 创建浮层
    function createPopperAction(e, payload) {
      // 创建 DOM 容器
      const container = document.createElement('div');
      container.className = 'stock-popover-container';
      container.style.zIndex = 999;
      // 设置标识，便于后期查找和销毁
      container.setAttribute('data-fullcode', e.target?.getAttribute('data-fullcode'));
      document.body.appendChild(container);

      // 高度变化时强制更新 Popper 位置
      const throttledUpdate = throttle(() => {
        el?.__popperInstance__?.update();
      }, 50);

      // 新增：监听弹框高度变化
      const resizeObserver = new ResizeObserver((entries) => {
        requestAnimationFrame(() => {
          // 获取弹框最新尺寸
          const { height } = entries[0].contentRect;
          // 仅当高度变化超过一定阈值（如2px）才更新，过滤微小波动
          if (Math.abs(height - (el.__lastHeight || 0)) > 2) {
            el.__lastHeight = height; // 缓存高度
            // 用 requestAnimationFrame 确保在重绘前更新位置
            requestAnimationFrame(() => {
              throttledUpdate();
            });
          }
        });
      });
      // 监听弹框容器的尺寸变化
      resizeObserver.observe(container);

      el.__lastHeight = 0; // 初始化缓存
      // 存储观察者实例，方便销毁时清理
      el.__resizeObserver__ = resizeObserver;

      const __popoverEl__mouseenter__ = () => {
        if (stockClick.delayTimer) clearTimeout(stockClick.delayTimer);
      };
      const __popoverEl__mouseleave__ = () => {
        onMouseout();
      };
      container.addEventListener('mouseenter', __popoverEl__mouseenter__);
      container.addEventListener('mouseleave', __popoverEl__mouseleave__);

      el.__popoverEl__mouseenter__ = __popoverEl__mouseenter__;
      el.__popoverEl__mouseleave__ = __popoverEl__mouseleave__;

      // 挂载 Vue 组件 监听CustomStocksPopover抛出的事件
      const app = createApp(CustomStocksPopover, { stock: payload || {}, hideAddStock: !!binding.value.hideAddStock });
      app.use(store);
      app.use(directives);
      app.use(router);
      app.use(i18n);
      app.use(ElementUI);
      app.mount(container);

      // 创建一个浮层显示股票信息
      const popperInstance = createPopper(e.target, container, popperOptions);

      // 显示浮层
      popperInstance.update();

      stockClick.hasPopover = true;

      el.__popoverApp__ = app;
      el.__popoverEl__ = container;
      el.__popoverTarget__ = e.target;
      el.__popperInstance__ = popperInstance;
    }
    // 添加事件监听
    function eventHandler(e) {
      const fullcode = e.target?.getAttribute('data-fullcode') || '';
      if (!fullcode) return;

      // 找一下已有的浮层，避免重复创建
      const hasPopoverDom = document.querySelector('.stock-popover-container');
      const popoverFullcode = hasPopoverDom?.getAttribute('data-fullcode') || '';

      // 如果已经有浮层且点击的是同一个股票 则不处理
      if (stockClick.hasPopover && popoverFullcode === fullcode) {
        if (stockClick.delayTimer) clearTimeout(stockClick.delayTimer);
        if (stockClick.showDelayTimer) clearTimeout(stockClick.showDelayTimer);
        return;
      }
      // 如果已经有浮层且点击的是不同股票 则销毁已有的
      if (stockClick.hasPopover && popoverFullcode !== fullcode) {
        hasPopoverDom?.remove();
        stockClick.hasPopover = false;
        if (stockClick.delayTimer) clearTimeout(stockClick.delayTimer);
        if (stockClick.showDelayTimer) clearTimeout(stockClick.showDelayTimer);
      }
      // 判断是否点击了指定的 class 元素 && 排除伪类元素
      if (e.target.classList.contains(binding.value.className)) {
        const payload = {};
        // 获取data-属性
        Array.from(e.target.attributes).forEach((attr) => {
          if (attr.name.startsWith('data-')) {
            payload[attr.name.slice(5)] = attr.value;
          }
        });

        // 清除之前的显示延迟定时器
        if (stockClick.showDelayTimer) {
          clearTimeout(stockClick.showDelayTimer);
          stockClick.showDelayTimer = null;
        }

        // 延迟显示 popover
        stockClick.showDelayTimer = setTimeout(() => {
          // 创建新的 popover
          createPopperAction(e, payload);

          // 点击外部关闭
          const outsideHandler = (clickEvent) => {
            if (
              el.__popoverEl__ &&
              !el.__popoverEl__.contains(clickEvent.target) &&
              !e.target.contains(clickEvent.target)
            ) {
              stockClick.destroyPopover(el, true);
            }
          };
          document.addEventListener('click', outsideHandler);
          el.__outsideHandler__ = outsideHandler;

          stockClick.showDelayTimer = null;
        }, stockClick.showDelay);
      }
    }
    // 鼠标移出后的处理
    function onMouseout() {
      // 清除显示延迟定时器，如果还在延迟期间
      if (stockClick.showDelayTimer) {
        clearTimeout(stockClick.showDelayTimer);
        stockClick.showDelayTimer = null;
      }
      if (stockClick.delayTimer) clearTimeout(stockClick.delayTimer);
      stockClick.delayTimer = setTimeout(() => {
        stockClick.destroyPopover(el, true);
      }, stockClick.hideDelay);
    }
    // 用于销毁前注销事件监听
    el.__mouseover__ = eventHandler;
    el.addEventListener('mouseenter', eventHandler, true);
    el.__mouseout__ = onMouseout;
    el.addEventListener('mouseleave', onMouseout, true);
  },
  beforeUnmount(el) {
    // 移除事件监听
    stockClick.destroyPopover(el);
  }
};

export default stockClick;
